import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Character from "../components/Character";
import StarBackground from "../components/StarBackground";
import { getTranslation } from '../config/translations';

export default function ExePage() {
    const navigate = useNavigate();
    const [currentLanguage, setCurrentLanguage] = useState(
        localStorage.getItem('selectedLanguage') || 'it'
    );
    
    const translations = getTranslation(currentLanguage);
    
    useEffect(() => {
        const handleLanguageChange = () => {
            const newLanguage = localStorage.getItem('selectedLanguage') || 'it';
            if (newLanguage !== currentLanguage) setCurrentLanguage(newLanguage);
        };
        const interval = setInterval(handleLanguageChange, 500);
        return () => clearInterval(interval);
    }, [currentLanguage]);
    
    const handleExerciseClick = (exerciseId) => {
        localStorage.setItem('exerciseId', exerciseId);
        localStorage.setItem('exercisePhase', 'intro');
        navigate(`/exercise/${exerciseId}/intro`);
    };
    
    return (
        <>
            <style>{`
/* =========================================
                   LAYOUT GENERALE (DESKTOP)
                   ========================================= */
                .page-wrapper {
                    min-height: 100vh;
                    width: 100%;
                    color: white;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    /* Allinea i contenuti partendo dall'alto, non dal centro */
                    justify-content: flex-start;
                    /* Spazio per l'header, evita che il testo ci finisca sotto */
                    padding-top: 120px;
                    padding-bottom: 50px;
                    font-family: 'Autour One', cursive;
                    text-align: center;
                    position: relative;
                    overflow-x: hidden;
                }

                .page-subtitle {
                    font-size: 32px;
                    color: #ccc;
                    /* Rimosso il margine negativo che causava il taglio */
                    margin-top: 0;
                    margin-bottom: 60px;
                    z-index: 20;
                    position: relative;
                }

                .planetSystem {
                    position: relative;
                    width: 100%;
                    max-width: 800px;
                    /* Altezza ridotta per adattarsi meglio a schermi panoramici o bassi */
                    height: 500px;
                    z-index: 10;
                }

                /* =========================================
                   STILI BASE PIANETI
                   ========================================= */
                .planetBtn {
                    position: absolute;
                    border-radius: 50%;
                    cursor: pointer;
                    border: none;
                    color: #fff;
                    font-family: 'Autour One', cursive;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    text-align: center;
                    padding: 0;
                    user-select: none;
                    box-shadow:
                        inset -15px -15px 0 rgba(0,0,0,0.15),
                        inset 10px 10px 0 rgba(255,255,255,0.2),
                        0 8px 15px rgba(0,0,0,0.3);
                    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.3s ease;
                    overflow: visible;
                }

                .planetLabel {
                    position: relative;
                    z-index: 20;
                    text-shadow: 0 2px 4px rgba(0,0,0,0.5);
                    pointer-events: none;
                    width: 80%;
                    transition: transform 0.3s ease;
                }

                /* Dimensioni Desktop riproporzionate */
                .pos-hiding { width: 130px; height: 130px; top: 0%; left: 50%; margin-left: -65px; font-size: 16px; }
                .pos-syllables { width: 160px; height: 160px; top: 50%; left: 5%; margin-top: -80px; font-size: 20px; }
                .pos-nonwords { width: 180px; height: 180px; bottom: 0%; left: 50%; margin-left: -90px; font-size: 24px; }
                .pos-sentences { width: 140px; height: 140px; top: 50%; right: 5%; margin-top: -70px; font-size: 18px; }

                /* Colori e animazioni Pianeti */
                .p-hiding {
                    background-color: #cbd5e1; color: #0f172a;
                    background-image: radial-gradient(circle at 30% 20%, rgba(71, 85, 105, 0.2) 0, transparent 25%), radial-gradient(circle at 70% 25%, rgba(51, 65, 85, 0.4) 6%, transparent 7%), radial-gradient(circle at 30% 65%, rgba(51, 65, 85, 0.3) 12%, transparent 15%), radial-gradient(circle at 75% 75%, rgba(51, 65, 85, 0.4) 5%, transparent 6%);
                    box-shadow: inset -20px -20px 60px rgba(15, 23, 42, 0.4);
                }
                .p-hiding .planetLabel { transform: rotate(0deg); text-shadow: none; }
                .p-hiding:hover { transform: scale(1.1) rotate(-10deg); filter: brightness(1.15); }
                .p-hiding:hover .planetLabel { transform: rotate(10deg); }

                .p-syllables {
                    background-color: #66BB6A;
                    background-image: linear-gradient(180deg, transparent 15%, rgba(27, 94, 32, 0.3) 15%, rgba(27, 94, 32, 0.3) 30%, transparent 30%, transparent 55%, rgba(200, 230, 201, 0.3) 55%, rgba(200, 230, 201, 0.3) 70%, transparent 70%);
                    background-size: 100% 100%; transform: rotate(-10deg);
                }
                .p-syllables .planetLabel { transform: rotate(10deg); }
                .p-syllables:hover { transform: scale(1.1) rotate(-20deg); filter: brightness(1.15); }
                .p-syllables:hover .planetLabel { transform: rotate(20deg); }

                .p-nonwords {
                    background-color: #4FC3F7; overflow: hidden; transform: rotate(10deg);
                }
                .p-nonwords::before, .p-nonwords::after { content: ''; position: absolute; background-color: #66BB6A; border-radius: 50%; }
                .p-nonwords::before { width: 60%; height: 50%; top: 10%; left: -10%; }
                .p-nonwords::after { width: 70%; height: 60%; bottom: -10%; right: -10%; }
                .p-nonwords .planetLabel { transform: rotate(-10deg); }
                .p-nonwords:hover { transform: scale(1.1) rotate(20deg); filter: brightness(1.15); }
                .p-nonwords:hover .planetLabel { transform: rotate(-20deg); }

                .p-sentences {
                    background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 30%, #3b82f6 50%, #1e40af 100%);
                    background-image: radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.8) 0%, transparent 60%), radial-gradient(ellipse at 30% 20%, rgba(147, 197, 253, 0.4) 0%, transparent 25%), radial-gradient(ellipse at 70% 60%, rgba(30, 58, 138, 0.6) 0%, transparent 30%), radial-gradient(circle at 40% 75%, rgba(96, 165, 250, 0.3) 0%, transparent 20%), radial-gradient(ellipse at 80% 30%, rgba(30, 64, 175, 0.5) 0%, transparent 25%), linear-gradient(135deg, #1e3a8a 0%, #2563eb 30%, #3b82f6 50%, #1e40af 100%);
                    transform: rotate(12deg);
                }
                .p-sentences .planetLabel { transform: rotate(-12deg); }
                .p-sentences:hover { transform: scale(1.15) rotate(30deg); filter: brightness(1.15); }
                .p-sentences:hover .planetLabel { transform: rotate(-28deg); }

                /* =========================================
                   RESPONSIVE DESIGN (TABLET, LAPTOP PICCOLI E SMARTPHONE)
                   ========================================= */
                @media (max-width: 992px), (max-height: 800px) {
                    .page-wrapper {
                        padding-top: 60px;
                    }

                    .page-subtitle {
                        /* Pushes the planets down slightly to balance the screen */
                        margin-bottom: 50px !important;
                        font-size: 26px !important;
                        padding: 0 15px;
                    }
                    
                    .planetSystem {
                        display: grid;
                        grid-template-columns: repeat(2, auto);
                        justify-content: center;
                        align-items: center;
                        /* Much bigger gaps to spread them out */
                        gap: 30px 40px;
                        height: auto;
                        padding-bottom: 120px;
                    }
                    
                    .planetBtn {
                        position: relative !important;
                        margin: 0 !important;
                        top: auto !important; bottom: auto !important;
                        left: auto !important; right: auto !important;
                    }
                    
                    /* Much LARGER sizes so they are easy to tap and read */
                    .pos-hiding { width: 160px; height: 160px; font-size: 18px; }
                    .pos-syllables { width: 170px; height: 170px; font-size: 20px; }
                    .pos-nonwords { width: 185px; height: 185px; font-size: 22px; }
                    .pos-sentences { width: 160px; height: 160px; font-size: 18px; }
                }

                /* Smartphone standard/stretti (es. iPhone) */
                @media (max-width: 450px) {
                    .page-subtitle {
                        font-size: 22px !important;
                        margin-bottom: 40px !important;
                    }
                    /* Slightly smaller gap for narrow screens */
                    .planetSystem { gap: 20px 20px; }
                    
                    /* Kept quite large, but safe for narrow screens */
                    .pos-hiding { width: 140px; height: 140px; font-size: 16px; }
                    .pos-syllables { width: 150px; height: 150px; font-size: 18px; }
                    .pos-nonwords { width: 165px; height: 165px; font-size: 20px; }
                    .pos-sentences { width: 140px; height: 140px; font-size: 16px; }
                }
            `}</style>
            
            <Header />
            
            {/* Ho rimosso "style={styles.wrapper}" e usato la classe */}
            <div className="page-wrapper">
                <StarBackground />
                
                <p className="page-subtitle">{translations.exeInstruction}</p>
                
                <div className="planetSystem">
                    <button
                        onClick={() => handleExerciseClick('hidingGame')}
                        className="planetBtn p-hiding pos-hiding"
                    >
                        <span className="planetLabel">{translations.exeHidingGame}</span>
                    </button>
                    
                    <button
                        onClick={() => handleExerciseClick('syllables')}
                        className="planetBtn p-syllables pos-syllables"
                    >
                        <span className="planetLabel">{translations.exeSyllables}</span>
                    </button>
                    
                    <button
                        onClick={() => handleExerciseClick('nonWords')}
                        className="planetBtn p-nonwords pos-nonwords"
                    >
                        <span className="planetLabel">{translations.exeNonWords}</span>
                    </button>
                    
                    <button
                        onClick={() => handleExerciseClick('sentences')}
                        className="planetBtn p-sentences pos-sentences"
                    >
                        <span className="planetLabel">{translations.exeSentences}</span>
                    </button>
                </div>
            </div>
            
            <Character name="Mars" />
        </>
    );
}

// L'oggetto styles non serve più, tutto è gestito elegantemente dalle classi nel <style>
